/* ============================================================================
   Joshua Perets — Search UX v3
   ----------------------------------------------------------------------------
   The <wd-search> element that drives the header field and its predictive
   dropdown. The engine underneath is unchanged: this still talks to native
   Shopify predictive search (Search & Discovery), it just renders the answer
   as one un-tabbed panel instead of a side drawer.

   DOM contract (see snippets/wd-search-bar.liquid + wd-search-dropdown.liquid):

     wd-search.jps
       form.jps-form                       one per breakpoint (desktop/mobile)
         input.jps-field__input[name=q]
         button.jps-field__clear
         button.jps-field__go              submits the form
       button.jps-cancel                   mobile only
       .jps-dropdown
         .jps-dropdown__inner              capped at the 1520px content width
           .jps-loading                    hairline progress bar
           .jps-panel[role=listbox]
             [data-jps-slot="left"]        categories → suggestions+collections
             [data-jps-slot="right"]       carousel → products
             .jps-panel__foot              trending, always
               [data-jps-slot="viewall"]

   The slots never unmount — only their contents swap — so the trending strip
   and the field do not repaint between keystrokes. The server-rendered empty
   state is cached at boot and restored verbatim when the field is cleared.

   Rows the keyboard walks are marked [data-jps-row] by the rendered section
   (suggestions and collections only — categories and chips are plain links).
   ============================================================================ */

(() => {
  'use strict';

  const MIN_CHARS = 3;
  const DEBOUNCE_MS = 150;
  const MIN_THUMB_PX = 36;

  const routes = (window.themeVariables && window.themeVariables.routes) || {};
  const SUGGEST_URL = routes.predictiveSearchUrl || '/search/suggest';
  const SEARCH_URL = routes.searchUrl || '/search';

  class WdSearch extends HTMLElement {
    connectedCallback() {
      if (this._booted) return;
      this._booted = true;

      this.forms = Array.from(this.querySelectorAll('.jps-form'));
      this.inputs = Array.from(this.querySelectorAll('.jps-field__input'));
      this.dropdown = this.querySelector('.jps-dropdown');
      this.panel = this.querySelector('.jps-panel');
      this.loadingEl = this.querySelector('.jps-loading');
      this.leftSlot = this.querySelector('[data-jps-slot="left"]');
      this.rightSlot = this.querySelector('[data-jps-slot="right"]');
      this.viewAll = this.querySelector('[data-jps-slot="viewall"]');

      // The empty state is merchandising, not search output — keep the markup
      // the server sent and put it back verbatim on clear.
      this.emptyLeft = this.leftSlot ? this.leftSlot.innerHTML : '';
      this.emptyRight = this.rightSlot ? this.rightSlot.innerHTML : '';

      this.sectionId = this.getAttribute('results-section') || 'wd-predictive-search';
      this.countSectionId = this.getAttribute('count-section') || 'wd-search-count';

      this.activeIndex = -1;
      this.rows = [];
      this.lastRendered = '';
      // The term of the request in flight. `lastRendered` only lands once the
      // answer is on screen, so it cannot tell a second opener that the query
      // it wants is already being fetched.
      this.pendingTerm = '';

      this._debouncedSearch = debounce(this._search.bind(this), DEBOUNCE_MS);

      // Opening on a field that already holds a query re-runs it. The results
      // PAGE ships the field pre-filled with `search.terms`, so without this
      // the panel would open on the empty state's popular carousel — offering
      // products for a query the page behind it has just said matched nothing.
      const reopen = () => {
        this.open();
        this._restoreQuery();
      };

      this.inputs.forEach((input) => {
        input.addEventListener('focus', reopen);
        input.addEventListener('click', reopen);
        input.addEventListener('input', () => this._onInput(input));
        input.addEventListener('keydown', (event) => this._onKeydown(event));
      });

      // The X in a section head of the no-results state. Close ONLY — the
      // query is deliberately left in the field, unlike the field's own X and
      // Cancel below, which clear it as well.
      //
      // Focus goes back to the input, so a keyboard dismiss does not drop the
      // shopper at the top of the document. That refocus would re-fire the
      // `focus` handler above and reopen what was just dismissed, so it is
      // guarded exactly the way Escape is.
      this.addEventListener('click', (event) => {
        if (!event.target.closest('[data-jps-close]')) return;
        event.preventDefault();
        this.close();
        this._suppressOpen = true;
        const input = this._visibleInput();
        if (input) input.focus();
        requestAnimationFrame(() => { this._suppressOpen = false; });
      });

      // There are exactly two ways to dismiss the panel: the X in the field,
      // and a click anywhere that is not the panel or the field — including
      // the rest of the header.
      //
      // Notably NOT focus leaving the input. `focusout` fires with a null
      // relatedTarget on every click on a non-focusable part of the panel — a
      // section heading, the padding between rows, the gap beside a card — so
      // treating it as "focus left the component" closed the dropdown while
      // the shopper was still browsing it.
      this.addEventListener('click', (event) => {
        if (!event.target.closest('.jps-field__clear, .jps-cancel')) return;
        event.preventDefault();
        this._clear();
        this.close();
      });

      // Click elsewhere. "Elsewhere" is measured against the PANEL AND THE
      // FIELD, not against <wd-search> — that element wraps the whole header,
      // so containment alone would treat the logo, the nav, the mega menus
      // and the cart icon as being inside the dropdown and leave it hanging
      // open behind whatever the shopper actually clicked.
      //
      // The field has to be excluded too: clicking the input opens the panel
      // on the way up, and a plain outside test would close it again on the
      // same click.
      this._onDocumentClick = (event) => {
        if (!this.open_) return;
        const keepOpen = event.target.closest('.jps-dropdown, .jps-form');
        if (keepOpen && this.contains(keepOpen)) return;
        this.close();
      };
      document.addEventListener('click', this._onDocumentClick);

      this._onDocumentKeydown = (event) => {
        if (event.key !== 'Escape' || !this.open_) return;
        this.close();
        this._suppressOpen = true;
        const input = this._visibleInput();
        if (input) input.focus();
        requestAnimationFrame(() => { this._suppressOpen = false; });
      };
      document.addEventListener('keydown', this._onDocumentKeydown);

      // Resize is handled once at module level for the whole document, which
      // covers this panel too.
      this._bindCarousels(this);
      this._bindFauxBar();

      // Land on /search?q=… with the query still in the field: the shopper
      // refines by editing, never by retyping.
      const params = new URLSearchParams(window.location.search);
      const term = params.get('q');
      if (term) this.inputs.forEach((input) => { if (!input.value) input.value = term; });
      this._syncChrome();

      // …and answer that query NOW, while the panel is still closed, instead of
      // waiting for the first click to ask. See _warm().
      this._warm();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocumentClick);
      document.removeEventListener('keydown', this._onDocumentKeydown);
      // Torn out while open (a theme-editor section re-render does exactly this)
      // would otherwise leave <html> locked with no element left to unlock it.
      if (this.lockedScroll) {
        this.lockedScroll = false;
        document.documentElement.classList.remove('jps-lock');
      }
      this._unbindViewport();
      this._unbindFauxBar();
    }

    /* ── the sheet's scrollbar ────────────────────────────────────────────
       An overlay <div> rather than ::-webkit-scrollbar, because iOS Safari
       draws its scroll indicators on the compositor and ignores that
       pseudo-element outright — the styled bar rendered on Android and simply
       was not there on iPhone. One mechanism for every mobile browser is easier
       to reason about than two that diverge per engine.

       Bound at every width, not just mobile: the panel is the same element
       across breakpoints, so binding once survives a resize or an orientation
       change with no rebinding. Desktop never shows it — the CSS lives inside
       the sheet media query, and at 1023px and up the panel has `overflow:
       visible` and no height cap, so _syncFauxBar() finds nothing to scroll. */

    _bindFauxBar() {
      if (!this.panel || !this.dropdown) return;

      this.fauxBar = document.createElement('div');
      this.fauxBar.className = 'jps-fauxbar';
      this.fauxBar.setAttribute('aria-hidden', 'true');
      (this.dropdown.querySelector('.jps-dropdown__inner') || this.dropdown).appendChild(this.fauxBar);
      this.dropdown.classList.add('jps-has-fauxbar');

      // One update per frame: touch scrolling fires continuously and each pass
      // reads layout.
      let queued = false;
      this._onPanelScroll = () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => { queued = false; this._syncFauxBar(); });
      };
      this.panel.addEventListener('scroll', this._onPanelScroll, { passive: true });

      // The slots never unmount, only their contents swap, so observing them
      // once covers every later result set — including product images that
      // settle after _afterRender() has already run.
      if (window.ResizeObserver) {
        this._barObserver = new ResizeObserver(() => this._syncFauxBar());
        Array.from(this.panel.children).forEach((child) => this._barObserver.observe(child));
      }
    }

    _unbindFauxBar() {
      if (this._onPanelScroll && this.panel) {
        this.panel.removeEventListener('scroll', this._onPanelScroll);
        this._onPanelScroll = null;
      }
      if (this._barObserver) {
        this._barObserver.disconnect();
        this._barObserver = null;
      }
      if (this.fauxBar) {
        this.fauxBar.remove();
        this.fauxBar = null;
      }
    }

    _syncFauxBar() {
      const bar = this.fauxBar;
      if (!bar || !this.panel) return;

      const view = this.panel.clientHeight;
      const total = this.panel.scrollHeight;

      // Shown only while the panel is open AND something actually overflows.
      // The open test is explicit rather than leaning on the dropdown's
      // `display: none`, because dropping the class on close is also what
      // re-arms the entrance pulse for the next open — CSS animations only
      // restart when the class comes back.
      //
      // The overflow test is what keeps the pill off desktop without a second
      // breakpoint to maintain: at 1023px and up the panel has `overflow:
      // visible` and no height cap, so total never exceeds view.
      if (!this.open_ || !view || total - view <= 1) {
        bar.classList.remove('is-on');
        return;
      }

      const height = Math.max(MIN_THUMB_PX, Math.round((view / total) * view));
      const travel = view - height;
      const offset = Math.round((this.panel.scrollTop / (total - view)) * travel);

      bar.style.height = height + 'px';
      bar.style.transform = 'translateY(' + Math.min(travel, Math.max(0, offset)) + 'px)';
      bar.classList.add('is-on');
    }

    /* ── open / close ─────────────────────────────────────────────────── */

    open() {
      // Escape closes the panel and returns focus to the input. Without this
      // guard that refocus would immediately reopen what was just dismissed.
      if (this.open_ || this._suppressOpen) return;
      this.open_ = true;
      this.classList.add('is-open');
      this.inputs.forEach((input) => input.setAttribute('aria-expanded', 'true'));

      // Above 1200px the panel does not hang off the bottom of this element —
      // .jps-full is absolutely positioned over the condensed row, so the
      // element's own box stops 55px short of the header you can see, and
      // wd-header-scroll.js makes up the difference in --wd-reserve. That
      // figure is measured, and a measurement taken before the viewport had
      // its final size (device-preview shells, a tab opened in the background)
      // would leave the panel sitting on top of the navigation. Re-take it
      // now, while the header is definitely laid out, and before _measure()
      // below reads the panel's top off it.
      if (window.wdHeaderMeasure) window.wdHeaderMeasure();

      this._measure();

      // The sheet covers the viewport below 1200px, so the page behind it
      // must not scroll. On desktop the page stays deliberately visible.
      if (window.matchMedia('(max-width: 1199px)').matches) {
        this.lockedScroll = true;
        document.documentElement.classList.add('jps-lock');
        this._bindViewport();
      }

      this._syncChrome();

      // The tracks were unmeasurable while the panel was display:none. Now
      // that it has a width, give the arrows their real state.
      this._syncCarousels();
    }

    close() {
      if (!this.open_) return;
      this.open_ = false;
      this.classList.remove('is-open');
      this.inputs.forEach((input) => input.setAttribute('aria-expanded', 'false'));

      // Only give back the lock we took — another drawer may be holding it.
      if (this.lockedScroll) {
        this.lockedScroll = false;
        document.documentElement.classList.remove('jps-lock');
        this._unbindViewport();
      }

      this._setActive(-1);
      this._syncChrome();
      // A request still in flight when the panel closed would never reach
      // _afterRender() if it is aborted, so the next open must not inherit the
      // hold — _restoreQuery() re-arms it if the term is still unanswered.
      this._setResolving(false);
      // Drops `is-on`, which both hides the pill and re-arms its entrance pulse
      // for the next open.
      this._syncFauxBar();
    }

    /* ── viewport measurement ─────────────────────────────────────────────
       Two numbers drive the sheet's height, and BOTH move when the soft
       keyboard animates in or out:

         --jps-panel-top  how far down the screen the sheet starts. It used to be
                          read once in open(), which on mobile is BEFORE the
                          keyboard has finished animating — so the cap was
                          computed from a top offset that was already stale by the
                          time the shopper could touch anything.
         --jps-vh         the height actually visible to the shopper. The keyboard
                          shrinks the visual viewport but not the layout viewport,
                          so this is the only figure that knows about it. See the
                          note on .jps-panel in wd-search.css.

       Kept current for as long as the sheet is open, then unbound on close so
       nothing listens while the search is idle. */
    _measure() {
      if (this.dropdown) {
        const top = Math.max(0, Math.round(this.dropdown.getBoundingClientRect().top));
        this.style.setProperty('--jps-panel-top', top + 'px');
      }
      const vv = window.visualViewport;
      if (vv) this.style.setProperty('--jps-vh', Math.round(vv.height) + 'px');
      // The cap just moved, so the thumb's scale is stale — the keyboard sliding
      // in or out is the loudest case.
      this._syncFauxBar();
    }

    _bindViewport() {
      const vv = window.visualViewport;
      if (!vv || this._onViewport) return;
      // Coalesce to one measure per frame: the keyboard animation fires these
      // events continuously, and each handler reads layout.
      let queued = false;
      this._onViewport = () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => { queued = false; if (this.open_) this._measure(); });
      };
      vv.addEventListener('resize', this._onViewport);
      vv.addEventListener('scroll', this._onViewport);
    }

    _unbindViewport() {
      const vv = window.visualViewport;
      if (!vv || !this._onViewport) return;
      vv.removeEventListener('resize', this._onViewport);
      vv.removeEventListener('scroll', this._onViewport);
      this._onViewport = null;
      // Hand the sheet back to its dvh fallback so a stale keyboard-sized
      // height cannot survive into the next open on a desktop-width resize.
      this.style.removeProperty('--jps-vh');
    }

    /* ── input ────────────────────────────────────────────────────────── */

    _visibleInput() {
      return this.inputs.find((input) => input.offsetParent !== null) || this.inputs[0];
    }

    _onInput(source) {
      const value = source.value;
      this.inputs.forEach((input) => { if (input !== source) input.value = value; });
      this._syncChrome();
      this._setActive(-1);
      // Editing hands the panel back to the typing rules: results stay on
      // screen until their replacement lands, exactly as before.
      this._setResolving(false);

      if (value.trim().length < MIN_CHARS) {
        this._abort();
        this._showEmpty();
        return;
      }

      this.open();
      this._debouncedSearch(value.trim());
    }

    /* Re-run whatever is already in the field when the panel opens.

       Focus and click both fire on the same tap, and the second one lands
       while the first one's request is still in flight — hence the pendingTerm
       guard as well as the rendered one, so that tap costs one fetch, not two.

       Below MIN_CHARS nothing is re-run and the empty state stands, which is
       the same rule typing follows. */
    _restoreQuery() {
      // The X and Escape both refocus the input on their way out, which fires
      // the focus handler again. `open()` refuses that one; there is no panel
      // to fill, so there is no request to make either.
      if (!this.open_) return;

      const input = this._visibleInput();
      const term = input ? input.value.trim() : '';
      if (term.length < MIN_CHARS) return;

      // Already on screen — _warm() usually got here first, which is what makes
      // the reopen render its answer on the same frame it opens.
      if (term === this.lastRendered) return;

      // Nothing has answered this term yet, so the slots still hold the empty
      // state: merchandising written for a shopper who has not asked anything,
      // and the wrong answer to the query sitting in the field. Hold it back
      // for the length of the request rather than let a dead query flash the
      // popular carousel on its way to "no products found".
      this._setResolving(true);
      if (term !== this.pendingTerm) this._search(term);
    }

    /* Resolve a prefilled query up front, at boot, so the FIRST open already
       has its answer in the slots.

       The results page ships the field carrying `search.terms`, so opening the
       panel there is always a reopen on a query the panel has never rendered —
       and paying for that request only once the shopper clicks is what put a
       request's worth of empty state between the click and the result.

       Costs nothing visible: the dropdown is display:none, so this lands
       silently, and by the time the panel opens _restoreQuery() finds
       `lastRendered` already matching and does nothing. Deferred to idle so it
       never competes with the page's own load. */
    _warm() {
      const input = this._visibleInput();
      const term = input ? input.value.trim() : '';
      if (term.length < MIN_CHARS) return;

      const run = () => {
        if (!this.isConnected) return;
        // Typing or a click may have moved the panel on before idle came
        // round; whatever they started is newer than this and wins.
        const current = this._visibleInput();
        if (!current || current.value.trim() !== term) return;
        if (this.lastRendered || this.pendingTerm) return;
        this._search(term);
      };

      if (window.requestIdleCallback) {
        window.requestIdleCallback(run, { timeout: 500 });
      } else {
        setTimeout(run, 0);
      }
    }

    /* Panel open on a term whose request has not landed yet. The class hides
       what the slots currently hold; the loading hairline sits outside the
       panel and stays visible. Cleared by _afterRender(), which every render
       path — results, empty and the error fallback — goes through. */
    _setResolving(on) {
      if (this.panel) this.panel.classList.toggle('is-resolving', !!on);
    }

    /* Clears the field and puts the panel back to its empty state. The caller
       decides whether to also close — the X and Cancel both do. */
    _clear() {
      this.inputs.forEach((input) => { input.value = ''; });
      this._abort();
      this._showEmpty();
      this._syncChrome();
    }

    /* The clear button only exists while there is something to clear. */
    _syncChrome() {
      const hasValue = this._visibleInput() && this._visibleInput().value.length > 0;
      this.querySelectorAll('.jps-field__clear').forEach((button) => {
        button.hidden = !hasValue;
      });
      this.querySelectorAll('.jps-field').forEach((field) => {
        field.classList.toggle('is-focus', !!this.open_);
      });
    }

    /* ── fetching ─────────────────────────────────────────────────────── */

    _abort() {
      if (this.controller) {
        this.controller.abort();
        this.controller = null;
      }
      this.pendingTerm = '';
      if (this.loadingEl) this.loadingEl.hidden = true;
    }

    async _search(term) {
      if (term === this.lastRendered) return;
      this._abort();
      this.pendingTerm = term;

      // A keystroke that lands mid-flight aborts the previous request, and an
      // aborted request still runs its own cleanup. This token is what stops
      // that stale cleanup from hiding the loading bar — or blanking the
      // panel — for the request that replaced it.
      const controller = new AbortController();
      this.controller = controller;
      const isCurrent = () => this.controller === controller;

      if (this.loadingEl) this.loadingEl.hidden = false;

      const url =
        SUGGEST_URL +
        '?q=' + encodeURIComponent(term) +
        '&resources[type]=product,collection,query' +
        '&resources[limit]=10' +
        '&resources[limit_scope]=each' +
        '&section_id=' + encodeURIComponent(this.sectionId);

      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error('Predictive search failed: ' + response.status);
        const html = await response.text();
        if (!isCurrent()) return;

        const holder = document.createElement('div');
        holder.innerHTML = html;

        const left = holder.querySelector('[data-jps-slot="left"]');
        const right = holder.querySelector('[data-jps-slot="right"]');
        const viewAll = holder.querySelector('[data-jps-slot="viewall"]');

        // Nothing matched. The panel keeps the layout it already has on screen —
        // same slots, same dimensions — and only the right column's content
        // changes, to the "no products found" message the section renders. The
        // left column falls back to the categories from the empty state and the
        // trending strip in the foot was never touched, so both of the ways out
        // of a dead query stay exactly where the shopper last saw them. No
        // second request: that merchandising never came from search.
        const recover = !!left && left.hasAttribute('data-jps-recover');

        if (this.leftSlot) {
          this.leftSlot.innerHTML = recover ? this.emptyLeft : (left ? left.innerHTML : '');
        }

        if (this.rightSlot) {
          this.rightSlot.innerHTML = right ? right.innerHTML : '';
          this._syncNoResults();
        }

        if (this.viewAll) {
          // No button when there is nothing to view — but the trending strip
          // stays, which is the cheapest way back into a working query.
          this.viewAll.hidden = recover || !viewAll;
          if (viewAll && !recover) {
            this.viewAll.href = viewAll.getAttribute('href');
            this.viewAll.textContent = viewAll.textContent.trim();
            const countTemplate = viewAll.getAttribute('data-jps-count');
            if (countTemplate) {
              this.viewAll.setAttribute('data-jps-count', countTemplate);
            } else {
              this.viewAll.removeAttribute('data-jps-count');
            }
          }
        }

        this.lastRendered = term;
        this._showResults(recover);
        if (!recover) this._fillCount(term);
      } catch (error) {
        if (error.name === 'AbortError' || !isCurrent()) return;
        // A failed request must never leave the shopper staring at a stale
        // panel: fall back to the entry points that always work.
        this._showEmpty();
        console.warn('[wd-search]', error);
      } finally {
        // Only the request still holding the slot clears it — a stale one that
        // was aborted mid-flight would otherwise wipe its replacement's term
        // and let the next opener refetch it.
        if (isCurrent()) this.pendingTerm = '';
        if (isCurrent() && this.loadingEl) this.loadingEl.hidden = true;
      }
    }

    /* Predictive search returns no total, so "View all results" ships without
       a number and the exact count is backfilled from the search page. Fired
       after the panel is already on screen — it never delays the results. */
    async _fillCount(term) {
      const target = this.viewAll;
      if (!target || target.hidden || !target.getAttribute('data-jps-count')) return;

      const url =
        SEARCH_URL +
        '?q=' + encodeURIComponent(term) +
        '&type=product' +
        '&section_id=' + encodeURIComponent(this.countSectionId);

      try {
        const response = await fetch(url, { signal: this.controller ? this.controller.signal : undefined });
        if (!response.ok) return;
        const text = await response.text();

        const count = (text.replace(/<[^>]*>/g, '').match(/\d+/) || [])[0];
        // The panel may have moved on while this was in flight.
        if (!count || term !== this.lastRendered) return;

        target.textContent = target.getAttribute('data-jps-count').replace('%count%', count);
      } catch (error) {
        /* No count is a fine outcome — the button already reads correctly. */
      }
    }

    /* ── state swap ───────────────────────────────────────────────────── */

    _showEmpty() {
      this.lastRendered = '';
      if (this.leftSlot) this.leftSlot.innerHTML = this.emptyLeft;
      if (this.rightSlot) {
        this.rightSlot.innerHTML = this.emptyRight;
        this._syncNoResults();
      }
      if (this.viewAll) this.viewAll.hidden = true;
      if (this.panel) this.panel.classList.remove('is-typed');
      this._afterRender();
    }

    /* The right column rendered the "no products" message instead of a card
       row, which is what the blush fill keys off. It goes on the COLUMN rather
       than on the message so it reaches the column's edges, and it has to be
       carried down to the sheet widths, where this column is otherwise white. */
    _syncNoResults() {
      if (!this.rightSlot) return;
      this.rightSlot.classList.toggle(
        'is-noresults',
        !!this.rightSlot.querySelector('.jps-caro--empty')
      );
    }

    _showResults(recover) {
      // No results means no `is-typed` either: that state's ordering exists to
      // put products above the fold with View all beneath them, and there are
      // no products. Dropping it leaves the empty state's own order —
      // categories, trending, then the right column carrying the message —
      // which is the layout the panel was already showing.
      if (this.panel) {
        this.panel.classList.toggle('is-typed', !recover);
      }
      this._afterRender();
    }

    _afterRender() {
      // Whatever is in the slots now is an answer to the term in the field, so
      // there is nothing left to hold back.
      this._setResolving(false);
      this._collectRows();
      if (!this.panel) return;
      this._bindCarousels(this.panel);
      this._syncCarousels();
      this._syncFauxBar();
    }

    /* ── keyboard (combobox) ──────────────────────────────────────────── */

    _collectRows() {
      this.rows = this.panel ? Array.from(this.panel.querySelectorAll('[data-jps-row]')) : [];
      this.rows.forEach((row, index) => {
        if (!row.id) row.id = 'jps-row-' + index;
      });
      this.activeIndex = -1;
    }

    _setActive(index) {
      if (this.rows[this.activeIndex]) this.rows[this.activeIndex].classList.remove('is-active');
      this.activeIndex = index;

      const row = this.rows[index];
      this.inputs.forEach((input) => {
        if (row) {
          input.setAttribute('aria-activedescendant', row.id);
        } else {
          input.removeAttribute('aria-activedescendant');
        }
      });

      if (!row) return;
      row.classList.add('is-active');
      row.scrollIntoView({ block: 'nearest' });
    }

    _onKeydown(event) {
      switch (event.key) {
        case 'ArrowDown':
          if (!this.rows.length) return;
          event.preventDefault();
          this.open();
          this._setActive((this.activeIndex + 1) % this.rows.length);
          break;

        case 'ArrowUp':
          if (!this.rows.length) return;
          event.preventDefault();
          this._setActive((this.activeIndex - 1 + this.rows.length) % this.rows.length);
          break;

        case 'Enter':
          // A highlighted row wins; otherwise the form submits whatever is in
          // the field, which is also what the mobile keyboard's Search key does.
          if (this.rows[this.activeIndex]) {
            event.preventDefault();
            this.rows[this.activeIndex].click();
          }
          break;
      }
    }

    /* ── carousels ────────────────────────────────────────────────────── */

    _bindCarousels(scope) {
      bindCarousels(scope);
    }

    _syncCarousels() {
      if (this.panel) syncCarousels(this.panel);
    }
  }

  /* ══ carousels ══════════════════════════════════════════════════════════
     Module level, NOT methods, because `.jps-caro` is rendered in two places:
     inside the header dropdown, and on the zero-results page, which is a
     different section entirely and nowhere near <wd-search>. Scoping this to
     the element left the page's arrows rendered but inert.
     ═════════════════════════════════════════════════════════════════════ */

  function bindCarousels(scope) {
    scope.querySelectorAll('.jps-caro').forEach((caro) => {
      if (caro.dataset.jpsBound) return;
      caro.dataset.jpsBound = '1';

      const track = caro.querySelector('.jps-caro__track');
      const prev = caro.querySelector('.jps-arrow--prev');
      const next = caro.querySelector('.jps-arrow--next');
      if (!track) return;

      const step = () => {
        const card = track.firstElementChild;
        return card ? card.offsetWidth + 16 : 180;
      };

      if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step() * 2, behavior: 'smooth' }));
      if (next) next.addEventListener('click', () => track.scrollBy({ left: step() * 2, behavior: 'smooth' }));

      const sync = () => {
        // A closed dropdown is display:none, so every measurement here comes
        // back 0 and the end-of-track test reads as "already at the end" —
        // which is what left both arrows dead until a resize happened to
        // re-measure them. An unmeasurable track gets no verdict at all.
        if (!track.clientWidth) return;
        const max = track.scrollWidth - track.clientWidth - 1;
        if (prev) prev.disabled = track.scrollLeft <= 0;
        if (next) next.disabled = track.scrollLeft >= max;
      };

      caro._jpsSync = sync;
      track.addEventListener('scroll', sync, { passive: true });
      sync();
    });
  }

  /* Re-measure. Needed once a track is actually on screen with a width, after
     any render, and on resize — the slides are a fraction of the track, so a
     breakpoint change moves the end of it. */
  function syncCarousels(scope) {
    scope.querySelectorAll('.jps-caro').forEach((caro) => {
      if (caro._jpsSync) caro._jpsSync();
    });
  }

  function debounce(fn, wait) {
    let timer = null;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  if (!window.customElements.get('wd-search')) {
    window.customElements.define('wd-search', WdSearch);
  }

  // Anything outside the header — today that is the zero-results page's
  // "Popular right now". Binding is idempotent (data-jps-bound), so it does
  // not matter whether this or the element gets to the header's own track
  // first.
  function initPageCarousels() {
    bindCarousels(document);
    syncCarousels(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPageCarousels);
  } else {
    initPageCarousels();
  }

  // Images landing late change scrollWidth, which decides whether the "next"
  // arrow is live.
  window.addEventListener('load', () => syncCarousels(document));
  window.addEventListener('resize', debounce(() => syncCarousels(document), 150));
})();
